/*
 * tp-byob-builder.js — builder page interactivity.
 *
 * The grid itself is rendered server-side in Liquid using the theme's own
 * card-product snippet, so the page matches the fonts collection exactly and
 * still works as a browsable grid without this script. All this adds is:
 * selection, search filtering, and the running bag.
 *
 * The bag is an uncommitted selection, not the cart — fonts only reach the
 * cart when the customer commits. Ladder maths comes from TPByob.ladder so
 * this page and the cart drawer can never disagree.
 */
if (!customElements.get('tp-byob-builder')) {
  customElements.define(
    'tp-byob-builder',
    class TpByobBuilder extends HTMLElement {
      connectedCallback() {
        if (!window.TPByob) return;

        this.bag = new Map();
        this.tiers = this.dataset.tiers || null;   // per-page ladder
        this.nouns = { one: this.dataset.nounOne || 'font', many: this.dataset.nounMany || 'fonts' };
        /* What the collection of chosen items is called. "bundle" on the fonts
           page, "bag" on the flash page. Defaults keep the font wording. */
        this.bagNoun = this.dataset.bagNoun || 'bundle';
        this.bagEmptyLabel = this.dataset.bagEmptyLabel || 'Build your bundle';

        this.gridEl = this.querySelector('[data-grid]');
        this.searchEl = this.querySelector('[data-search]');
        this.emptyEl = this.querySelector('[data-empty]');
        this.bagEl = this.querySelector('[data-bag]');
        this.bagToggleEl = this.querySelector('[data-bag-toggle]');
        this.bagToggleIconEl = this.querySelector('[data-bag-toggle-icon]');
        this.bagMobileSummaryEl = this.querySelector('[data-bag-mobile-summary]');
        this.bagListEl = this.querySelector('[data-bag-list]');
        this.bagCountEl = this.querySelector('[data-bag-count]');
        this.bagTotalEl = this.querySelector('[data-bag-total]');
        this.bagSavingsEl = this.querySelector('[data-bag-savings]');
        this.bagPerFontEl = this.querySelector('[data-bag-per-font]');
        this.bagMessageEl = this.querySelector('[data-bag-message]');
        this.addBtn = this.querySelector('[data-add-all]');

        this.picks = Array.from(this.querySelectorAll('[data-pick]'));
        this.bindMobileAccordion();
        this.bindPicks();
        this.bindControls();
        this.bindBagToggle();
        this.renderBag();
      }

      /* The theme's animated details handler can race with native touch
         clicks on iOS. This page only needs a simple, immediate accordion on
         small screens, so handle those taps before the theme listener does. */
      bindMobileAccordion() {
        if (
          !document.body.classList.contains('build-your-own') ||
          document.documentElement.dataset.tpByobMobileAccordion
        ) {
          return;
        }

        document.documentElement.dataset.tpByobMobileAccordion = 'true';
        var nativeOpen = Object.getOwnPropertyDescriptor(HTMLDetailsElement.prototype, 'open');

        var setOpenImmediately = function (details, isOpen) {
          /* The theme's customized details setter animates for 250ms. Keep its
             internal state in sync, but use the native attribute setter so a
             mobile tap does not wait on that animation. */
          details.isOpen = isOpen;
          if (nativeOpen && nativeOpen.set) {
            nativeOpen.set.call(details, isOpen);
          } else if (isOpen) {
            details.setAttribute('open', '');
          } else {
            details.removeAttribute('open');
          }
          details.style.height = 'auto';
          details.style.overflow = 'visible';
          var content = details.querySelector('.accordion-details__content');
          if (content) {
            content.style.opacity = isOpen ? '1' : '0';
            content.style.transform = 'none';
          }
          details.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
          details.dispatchEvent(new Event('toggle'));
        };

        var touchState = null;

        var findSummary = function (target) {
          if (!target || !target.closest) return null;
          return target.closest(
            '.section--collapsible-tabs details.accordion-details > summary'
          );
        };

        var toggleSummary = function (summary, event) {
          var details = summary.parentElement;
          var parent = details.closest('.accordion-parent');
          if (!parent) return;

          event.preventDefault();
          event.stopImmediatePropagation();

          var nextOpen = !details.isOpen;
          parent.querySelectorAll('details[is="accordion-group"]').forEach(function (sibling) {
            setOpenImmediately(sibling, sibling === details && nextOpen);
          });
        };

        document.addEventListener('pointerdown', function (event) {
          if (
            !window.matchMedia('(max-width: 767px)').matches ||
            event.pointerType !== 'touch' ||
            !event.isPrimary
          ) return;

          var summary = findSummary(event.target);
          if (!summary) return;
          touchState = {
            summary: summary,
            x: event.clientX,
            y: event.clientY,
            moved: false
          };
        }, true);

        document.addEventListener('pointermove', function (event) {
          if (!touchState || event.pointerType !== 'touch') return;
          if (Math.hypot(event.clientX - touchState.x, event.clientY - touchState.y) > 10) {
            touchState.moved = true;
          }
        }, true);

        document.addEventListener('pointerup', function (event) {
          if (!touchState || event.pointerType !== 'touch' || !event.isPrimary) return;
          var state = touchState;
          touchState = null;

          if (state.moved) {
            state.summary.__tpByobAccordionSuppressClickUntil = Date.now() + 500;
            return;
          }

          state.summary.__tpByobAccordionHandledUntil = Date.now() + 500;
          toggleSummary(state.summary, event);
        }, true);

        document.addEventListener('pointercancel', function () {
          if (touchState) {
            touchState.summary.__tpByobAccordionSuppressClickUntil = Date.now() + 500;
          }
          touchState = null;
        }, true);

        document.addEventListener('click', function (event) {
          if (!window.matchMedia('(max-width: 767px)').matches) return;
          var summary = findSummary(event.target);
          if (!summary) return;

          var now = Date.now();
          if (
            (summary.__tpByobAccordionHandledUntil && now < summary.__tpByobAccordionHandledUntil) ||
            (summary.__tpByobAccordionSuppressClickUntil && now < summary.__tpByobAccordionSuppressClickUntil)
          ) {
            event.preventDefault();
            event.stopImmediatePropagation();
            delete summary.__tpByobAccordionHandledUntil;
            delete summary.__tpByobAccordionSuppressClickUntil;
            return;
          }

          toggleSummary(summary, event);
        }, true);
      }

      bindPicks() {
        this.picks.forEach(function (pick) {
          pick.setAttribute('role', 'button');
          pick.setAttribute('tabindex', '0');
          pick.setAttribute('aria-pressed', 'false');

          /* The card contains a link to the product. On this page the click
             means "add to my bundle", so suppress the navigation. */
          pick.addEventListener('click', function (event) {
            /* A touch pointer is handled on pointerup below. Ignore the
               synthetic click that iOS may dispatch afterward. */
            if (pick.__tpTouchHandledUntil && Date.now() < pick.__tpTouchHandledUntil) {
              event.preventDefault();
              delete pick.__tpTouchHandledUntil;
              return;
            }
            var link = event.target.closest('a');
            if (link) event.preventDefault();
            this.toggle(pick);
          }.bind(this));

          pick.addEventListener('pointerdown', function (event) {
            if (event.pointerType !== 'touch' || !event.isPrimary) return;
            pick.__tpTouchStart = { x: event.clientX, y: event.clientY, moved: false };
          });

          pick.addEventListener('pointermove', function (event) {
            var start = pick.__tpTouchStart;
            if (!start || event.pointerType !== 'touch') return;
            start.moved = Math.hypot(event.clientX - start.x, event.clientY - start.y) > 10;
          });

          pick.addEventListener('pointerup', function (event) {
            var start = pick.__tpTouchStart;
            pick.__tpTouchStart = null;
            if (!start || start.moved || event.pointerType !== 'touch' || !event.isPrimary) return;

            event.preventDefault();
            pick.__tpTouchHandledUntil = Date.now() + 500;
            this.toggle(pick);
          }.bind(this));

          pick.addEventListener('pointercancel', function () {
            pick.__tpTouchStart = null;
          });

          pick.addEventListener('keydown', function (event) {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              this.toggle(pick);
            }
          }.bind(this));
        }.bind(this));
      }

      toggle(pick) {
        var key = pick.dataset.handle;
        if (this.bag.has(key)) {
          this.bag.delete(key);
        } else {
          this.bag.set(key, {
            handle: key,
            title: pick.dataset.title,
            variantId: Number(pick.dataset.variantId),
            price: Number(pick.dataset.price)
          });
        }
        var selected = this.bag.has(key);
        pick.classList.toggle('is-selected', selected);
        pick.setAttribute('aria-pressed', selected ? 'true' : 'false');
        this.renderBag();
      }

      bindControls() {
        if (this.searchEl) {
          this.searchEl.addEventListener('input', function () {
            var q = this.searchEl.value.trim().toLowerCase();
            var visible = 0;
            this.picks.forEach(function (pick) {
              var hit = !q || pick.dataset.title.toLowerCase().indexOf(q) !== -1;
              pick.hidden = !hit;
              if (hit) visible++;
            });
            if (this.emptyEl) this.emptyEl.hidden = visible !== 0;
          }.bind(this));
        }

        if (this.addBtn) {
          this.addBtn.addEventListener('click', this.addAllToCart.bind(this));
        }
      }

      bindBagToggle() {
        if (!this.bagEl) return;
        this.bagEl.addEventListener('toggle', this.updateBagToggle.bind(this));
        this.updateBagToggle();
      }

      updateBagToggle() {
        if (!this.bagToggleEl || !this.bagEl) return;
        var expanded = this.bagEl.open;
        this.bagEl.classList.toggle('is-expanded', expanded);
        this.bagToggleEl.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        this.bagToggleEl.setAttribute('aria-label', (expanded ? 'Hide ' : 'Show ') + this.bagNoun + ' details');
        if (this.bagToggleIconEl) this.bagToggleIconEl.textContent = expanded ? '↓' : '↑';
      }

      renderBag() {
        var items = Array.from(this.bag.values());

        var state = window.TPByob.ladder(items.map(function (f) { return f.price; }), this.tiers, this.nouns);

        if (this.bagEl) {
          var wasVisible = this.bagEl.classList.contains('is-visible');
          var isMobile = window.matchMedia('(max-width: 989px)').matches;
          this.bagEl.classList.toggle('is-visible', items.length > 0);
          if (isMobile && (!items.length || (!wasVisible && items.length > 0))) {
            this.bagEl.removeAttribute('open');
          }
        }
        if (this.bagMobileSummaryEl) {
          this.bagMobileSummaryEl.textContent = items.length
            ? items.length + ' in ' + this.bagNoun + ' · Add ' + items.length + ' to cart'
            : this.bagEmptyLabel;
        }
        this.updateBagToggle();
        if (this.bagCountEl) this.bagCountEl.textContent = items.length;
        if (this.bagTotalEl) this.bagTotalEl.textContent = items.length ? state.format(state.payable) : '—';
        if (this.bagMessageEl) this.bagMessageEl.textContent = state.message;
        if (this.bagSavingsEl) this.bagSavingsEl.textContent = state.savings ? state.format(state.savings) : '—';
        if (this.bagPerFontEl) {
          this.bagPerFontEl.textContent = items.length ? state.format(state.effectivePerFont) : '—';
        }
        if (this.addBtn) {
          this.addBtn.disabled = items.length === 0;
          this.addBtn.textContent = items.length ? 'Add ' + items.length + ' to cart' : 'Add to cart';
        }

        if (this.bagListEl) {
          this.bagListEl.innerHTML = '';
          items.forEach(function (font) {
            var li = document.createElement('li');
            li.className = 'tp-byob-bag__item';

            var label = document.createElement('span');
            label.textContent = font.title;
            li.appendChild(label);

            var remove = document.createElement('button');
            remove.type = 'button';
            remove.className = 'tp-byob-bag__remove';
            remove.setAttribute('aria-label', 'Remove ' + font.title);
            remove.textContent = '×';
            remove.addEventListener('click', function () {
              this.bag.delete(font.handle);
              var pick = this.gridEl.querySelector('[data-handle="' + font.handle + '"]');
              if (pick) {
                pick.classList.remove('is-selected');
                pick.setAttribute('aria-pressed', 'false');
              }
              this.renderBag();
            }.bind(this));
            li.appendChild(remove);

            this.bagListEl.appendChild(li);
          }.bind(this));
        }
      }

      addAllToCart() {
        var items = Array.from(this.bag.values()).map(function (f) {
          return { id: f.variantId, quantity: 1 };
        });
        if (!items.length) return;

        this.addBtn.disabled = true;
        this.addBtn.textContent = 'Adding…';

        window.TPByob.addToCart(items)
          .then(function () {
            window.TPByob.track('byob_bag_added_to_cart', { count: items.length });
            this.clearBag();
          }.bind(this))
          .catch(function () {
            this.addBtn.disabled = false;
            this.renderBag();
          }.bind(this));
      }

      clearBag() {
        this.bag.clear();
        this.picks.forEach(function (pick) {
          pick.classList.remove('is-selected');
          pick.setAttribute('aria-pressed', 'false');
        });
        this.renderBag();
      }
    }
  );
}
